import React from 'react';
import Seo from '../components/Seo';
import { Link } from 'react-router-dom';
import { Flame, ArrowRight, Mic2, Phone, ShoppingBag, Quote, Sparkles, BookOpen, Shirt } from 'lucide-react';
import { BRAND, REFINE, TESTIMONIALS, EXPERIENCE_MOMENTS } from '../mock/mock';
import Reveal from '../components/Reveal';

const Home = () => {
  return (
    <div className="bg-fire-ink">
      <Seo path="/" description="Tested by the Fire. Refined by the Flame.™ After the Fire with Dr. Gee helps women move from surviving loss and trauma to intentionally rebuilding — through live events, Fireside Clarity Calls, original music and the Flame Shop." />
      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-24 pb-16 overflow-hidden ember-texture">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full opacity-30 blur-3xl" style={{ background: 'radial-gradient(circle, rgba(217,117,36,0.35), transparent 60%)' }} />
        </div>
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 relative grid lg:grid-cols-2 gap-12 items-center w-full">
          <div className="animate-fade-up">
            <div className="inline-flex items-center gap-2 border border-fire-gold/30 rounded-full px-4 py-1.5 mb-6">
              <Flame className="w-3.5 h-3.5 text-fire-gold" />
              <span className="font-marcellus tracking-[0.22em] text-[11px] text-fire-goldlight">THE MOVEMENT FOR WOMEN</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-[4.1rem] leading-[1.05] text-fire-cream mb-6">
              Sometimes Plan A<br />burns down.
              <span className="block text-gradient-gold mt-2">We help you build<br />what comes next.</span>
            </h1>
            <p className="text-fire-cream/70 font-body text-base md:text-lg max-w-xl mb-4 leading-relaxed">
              {BRAND.promise}
            </p>
            <p className="font-cormorant italic text-fire-goldlight text-xl mb-8">
              “{BRAND.declaration}”
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="btn-gold rounded-full px-7 py-3.5 font-semibold inline-flex items-center gap-2 text-sm">
                Contact Dr. Gee Today <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/experience" className="btn-outline-gold rounded-full px-7 py-3.5 font-semibold text-sm">
                Explore the Experience
              </Link>
            </div>
            <p className="font-marcellus tracking-[0.25em] text-fire-cream/45 text-[11px] mt-9">
              {BRAND.tagline}
            </p>
          </div>

          <div className="relative animate-fade-up" style={{ animationDelay: '150ms' }}>
            <div className="relative mx-auto max-w-[440px]">
              <div className="absolute inset-0 rounded-full blur-2xl opacity-70 ember-glow" />
              <div className="relative rounded-3xl overflow-hidden bg-fire-cream p-6 shadow-2xl">
                <img src={BRAND.logo} alt="After the Fire logo" className="w-full h-auto" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT DO YOU NEED TODAY */}
      <section className="py-20 md:py-24 bg-fire-inksoft border-y border-fire-gold/10">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8">
          <Reveal className="text-center mb-12">
            <span className="font-marcellus tracking-[0.25em] text-fire-gold text-xs">START HERE</span>
            <h2 className="font-display text-3xl md:text-5xl text-fire-cream mt-3">What Do You Need Today?</h2>
            <p className="text-fire-cream/60 font-body mt-4 max-w-xl mx-auto">Don’t worry about figuring out where to begin. Just tell us what you need — we’ll take you there.</p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Phone, need: 'I Need Clarity', cta: 'Book a Fireside Call', to: '/fireside' },
              { icon: Mic2, need: 'I Need the Experience', cta: 'Bring ATF to My Organization', to: '/book' },
              { icon: BookOpen, need: 'I Need to Rebuild', cta: 'Get the Book + Journal', to: '/shop' },
              { icon: Shirt, need: 'I Survived', cta: 'Shop the Collection', to: '/shop' },
            ].map((c, i) => (
              <Reveal key={c.need} delay={i * 80}>
                <Link
                  to={c.to}
                  data-testid={`need-card-${i}`}
                  className="card-dark rounded-2xl p-7 h-full hover-lift group flex flex-col items-start border border-fire-gold/15 hover:border-fire-gold/40 transition-colors"
                >
                  <div className="w-14 h-14 rounded-full bg-fire-maroon/30 border border-fire-gold/30 flex items-center justify-center mb-6 group-hover:bg-fire-gold/20 transition-colors">
                    <c.icon className="w-6 h-6 text-fire-goldlight" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-2xl text-fire-cream mb-3 leading-tight">{c.need}</h3>
                  <span className="mt-auto text-fire-goldlight font-body text-sm font-semibold inline-flex items-center gap-2 group-hover:gap-3 transition-all">
                    {c.cta} <ArrowRight className="w-4 h-4 shrink-0" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SIGNATURE STATEMENT STRIP */}
      <section className="bg-gradient-to-r from-fire-maroondark via-fire-maroon to-fire-maroondark py-8">
        <div className="max-w-[1100px] mx-auto px-6 text-center">
          <p className="font-cormorant text-2xl md:text-3xl text-fire-cream italic">
            “There’s an exit. <span className="text-fire-goldlight">Come this way.</span>”
          </p>
        </div>
      </section>

      {/* MEET DR GEE INTRO */}
      <section className="py-24 ember-texture">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal className="relative">
            <div className="relative rounded-2xl overflow-hidden hover-lift">
              <img src="/brand/mischell-couch.jpg" alt="Dr. Mischell Gee" className="w-full h-auto" />
            </div>
            <div className="hidden md:block absolute -bottom-6 -right-6 card-dark rounded-xl px-6 py-4 shadow-xl">
              <p className="font-script text-3xl text-fire-goldlight leading-none">Dr. Gee</p>
              <p className="font-body text-xs text-fire-cream/60 mt-1 tracking-wide">Pastor • Author • Recording Artist</p>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <span className="font-marcellus tracking-[0.25em] text-fire-gold text-xs">MEET DR. MISCHELL “DR. GEE” GLENN</span>
            <h2 className="font-display text-3xl md:text-4xl text-fire-cream mt-4 mb-6 leading-tight">
              She doesn’t just tell a story.<br />She brings women face-to-face with their own.
            </h2>
            <p className="text-fire-cream/70 font-body leading-relaxed mb-5">
              With a message born from real-life experiences of abandonment, heartbreak, single motherhood, broken relationships and rebuilding, Dr. Gee speaks with the kind of transparency that makes women say, <span className="text-fire-goldlight italic">“She’s talking to me.”</span>
            </p>
            <p className="text-fire-cream/70 font-body leading-relaxed mb-8">
              Storytelling, music, faith, humor and practical rebuilding tools come together to create an experience women remember long after the event is over.
            </p>
            <Link to="/my-story" className="btn-outline-gold rounded-full px-6 py-3 font-semibold inline-flex items-center gap-2 text-sm">
              Read My Story <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* REFINE FRAMEWORK */}
      <section className="py-24 bg-fire-inksoft">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <Sparkles className="w-7 h-7 text-fire-gold mx-auto mb-4" />
            <span className="font-marcellus tracking-[0.25em] text-fire-gold text-xs">THE OFFICIAL FRAMEWORK</span>
            <h2 className="font-display text-3xl md:text-5xl text-fire-cream mt-3">The R.E.F.I.N.E.™ Journey</h2>
            <p className="text-fire-cream/60 font-body mt-4">Six movements from surviving what happened to intentionally rebuilding what comes next.</p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {REFINE.map((r, idx) => (
              <Reveal key={idx} delay={idx * 60}>
                <div className="card-dark rounded-2xl p-7 h-full hover-lift group">
                  <div className="flex items-start gap-4">
                    <span className="font-display text-5xl text-gradient-gold leading-none">{r.letter}</span>
                    <div>
                      <h3 className="font-marcellus text-fire-cream tracking-wide text-sm mb-2 uppercase">{r.title}</h3>
                      <p className="text-fire-cream/60 font-body text-sm leading-relaxed">{r.text}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* THREE PATHS */}
      <section className="py-24 ember-texture">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8">
          <Reveal className="text-center mb-14">
            <span className="font-marcellus tracking-[0.25em] text-fire-gold text-xs">FIND YOUR WAY IN</span>
            <h2 className="font-display text-3xl md:text-5xl text-fire-cream mt-3">Come This Way</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Mic2, title: 'The ATF Experience', text: 'Bring the After the Fire™ Experience to your city, church, conference or retreat.', to: '/experience', cta: 'Explore' },
              { icon: Phone, title: 'Fireside Clarity Calls', text: 'A private, one-on-one conversation with Dr. Gee — a safe place to hear yourself think.', to: '/fireside', cta: 'Book a Call' },
              { icon: ShoppingBag, title: 'The Flame Shop', text: 'Wear your testimony. Shop the Survivor & Firehouse collections, books and music.', to: '/shop', cta: 'Shop Now' },
            ].map((c, i) => (
              <Reveal key={i} delay={i * 90}>
                <Link to={c.to} className="card-dark rounded-2xl p-8 h-full hover-lift group flex flex-col items-start">
                  <div className="w-14 h-14 rounded-full bg-fire-maroon/30 border border-fire-gold/30 flex items-center justify-center mb-5 group-hover:bg-fire-gold/20 transition-colors">
                    <c.icon className="w-6 h-6 text-fire-goldlight" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-2xl text-fire-cream mb-3">{c.title}</h3>
                  <p className="text-fire-cream/60 font-body text-sm leading-relaxed mb-6 flex-1">{c.text}</p>
                  <span className="text-fire-goldlight font-body text-sm font-semibold inline-flex items-center gap-2 group-hover:gap-3 transition-all">
                    {c.cta} <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED BOOK */}
      <section className="py-24 bg-fire-inksoft overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal className="flex justify-center">
            <img src="/brand/book-front.jpg" alt="After the Fire novel" className="w-[300px] md:w-[360px] h-auto drop-shadow-2xl hover-lift" />
          </Reveal>
          <Reveal delay={120}>
            <span className="font-marcellus tracking-[0.25em] text-fire-gold text-xs">THE BOOK</span>
            <h2 className="font-display text-3xl md:text-5xl text-fire-cream mt-3 mb-3">After the Fire</h2>
            <p className="font-cormorant italic text-fire-goldlight text-2xl mb-6">Some fires don’t destroy you. They redefine you.</p>
            <p className="text-fire-cream/70 font-body leading-relaxed mb-8">
              The raw, real and powerful account of one woman’s journey through abandonment, loss and life’s darkest moments — and how she found the strength to rise. This book is for every woman who has been through the fire and is wondering if she can make it. You can.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/shop" className="btn-gold rounded-full px-7 py-3.5 font-semibold inline-flex items-center gap-2 text-sm">
                Get the Book <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/shop" className="btn-outline-gold rounded-full px-7 py-3.5 font-semibold text-sm">
                Prayer Journal & Music
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* EXPERIENCE PREVIEW */}
      <section className="py-24 ember-texture">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <span className="font-marcellus tracking-[0.25em] text-fire-gold text-xs">SIGNATURE MOMENTS</span>
            <h2 className="font-display text-3xl md:text-5xl text-fire-cream mt-3">This Isn’t Just an Event.</h2>
            <p className="text-fire-cream/60 font-body mt-4">It’s an experience where testimony meets music and laughter can sit beside tears.</p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {EXPERIENCE_MOMENTS.slice(0, 3).map((m, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className="card-dark rounded-2xl p-7 h-full hover-lift">
                  <Flame className="w-6 h-6 text-fire-gold mb-4" strokeWidth={1.5} />
                  <h3 className="font-display text-xl text-fire-cream mb-3">{m.title}</h3>
                  <p className="text-fire-cream/60 font-body text-sm leading-relaxed">{m.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/experience" className="btn-outline-gold rounded-full px-7 py-3.5 font-semibold inline-flex items-center gap-2 text-sm">
              See the Full Experience <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 bg-fire-inksoft">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={i} delay={i * 90}>
                <div className="card-dark rounded-2xl p-8 h-full">
                  <Quote className="w-8 h-8 text-fire-gold/60 mb-4" />
                  <p className="font-cormorant text-xl text-fire-cream/90 italic leading-relaxed mb-6">“{t.quote}”</p>
                  <p className="font-marcellus tracking-widest text-fire-goldlight text-xs">{t.name}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-fire-maroondark via-fire-ink to-fire-maroondark" />
        <div className="absolute inset-0 opacity-20" style={{ background: 'radial-gradient(circle at 50% 120%, #d97524, transparent 55%)' }} />
        <div className="relative max-w-[900px] mx-auto px-6 text-center">
          <Reveal>
            <Flame className="w-10 h-10 text-fire-gold mx-auto mb-6 animate-flicker" strokeWidth={1.4} />
            <h2 className="font-display text-3xl md:text-5xl text-fire-cream mb-5 leading-tight">
              You survived the fire.<br />Now discover the woman who walked out.
            </h2>
            <p className="text-fire-cream/70 font-body mb-8 max-w-xl mx-auto">
              Whether you’re ready for a room full of women or a quiet one-on-one conversation — let’s talk about what comes next.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/contact" className="btn-gold rounded-full px-8 py-4 font-semibold inline-flex items-center gap-2">
                Contact Dr. Gee Today <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/book" className="btn-outline-gold rounded-full px-8 py-4 font-semibold">
                Book Dr. Gee
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
